/**
 * PythonLinter.js
 * Linter Python utilisant Pyodide (Python WASM) pour une validation réelle via ast.parse + py_compile.
 * Fallback sur un validateur JS basique si Pyodide n'est pas disponible.
 */

let pyodideInstance = null;
let loadingPromise = null;

async function loadPyodideFromCDN() {
    if (pyodideInstance) return pyodideInstance;
    if (loadingPromise) return loadingPromise;

    loadingPromise = (async () => {
        try {
            // Charger Pyodide depuis le CDN (pas de package npm — incompatible webpack)
            if (!window.loadPyodide) {
                await new Promise((resolve, reject) => {
                    const script = document.createElement("script");
                    script.src = "https://cdn.jsdelivr.net/pyodide/v0.27.7/full/pyodide.js";
                    script.onload = resolve;
                    script.onerror = reject;
                    document.head.appendChild(script);
                });
            }
            // eslint-disable-next-line no-undef
            pyodideInstance = await window.loadPyodide({
                indexURL: "https://cdn.jsdelivr.net/pyodide/v0.27.7/full/",
            });
            // Préparer le helper de lint
            pyodideInstance.runPython(`
import ast
import py_compile
import io
import sys
import traceback

def lint_code(code):
    errors = []
    # 1. ast.parse — erreurs de syntaxe
    try:
        ast.parse(code)
    except SyntaxError as e:
        errors.append({"line": e.lineno or 1, "col": e.offset or 0, "msg": str(e.msg), "severity": "error"})
        return errors  # pas la peine de continuer

    # 2. Analyse AST pour warnings
    try:
        tree = ast.parse(code)
        # Vérifier les variables non utilisées (basique)
        assigned = set()
        used = set()
        for node in ast.walk(tree):
            if isinstance(node, ast.Assign):
                for target in node.targets:
                    if isinstance(target, ast.Name):
                        assigned.add(target.id)
            elif isinstance(node, ast.Name) and isinstance(node.ctx, ast.Load):
                used.add(node.id)
        unused = assigned - used - {'_', '__all__', 'value', 'delta'}
        for name in sorted(unused):
            # Trouver la ligne
            for node in ast.walk(tree):
                if isinstance(node, ast.Assign):
                    for t in node.targets:
                        if isinstance(t, ast.Name) and t.id == name:
                            errors.append({"line": t.lineno, "col": 0, "msg": f"Variable '{name}' assignee mais non utilisee", "severity": "warning"})

        # Vérifier les imports inutilisés
        for node in ast.walk(tree):
            if isinstance(node, ast.Import):
                for alias in node.names:
                    n = alias.asname or alias.name.split('.')[0]
                    if n not in used and n not in ('np', 'pd', 'scipy', 'warnings'):
                        errors.append({"line": node.lineno, "col": 0, "msg": f"Import '{n}' non utilise", "severity": "warning"})
            elif isinstance(node, ast.ImportFrom):
                for alias in node.names:
                    n = alias.asname or alias.name
                    if n not in used:
                        errors.append({"line": node.lineno, "col": 0, "msg": f"Import '{n}' de '{node.module}' non utilise", "severity": "warning"})
    except Exception:
        pass

    return errors
`);
            return pyodideInstance;
        } catch (e) {
            console.warn("[PythonLinter] Pyodide non disponible, fallback JS", e);
            loadingPromise = null;
            return null;
        }
    })();

    return loadingPromise;
}

/**
 * Lint Python code — utilise Pyodide si disponible, sinon fallback JS.
 * @returns {Promise<Array<{line: number, col: number, msg: string, severity: "error"|"warning"}>>}
 */
export async function lintPython(code) {
    const pyodide = await loadPyodideFromCDN();

    if (pyodide) {
        try {
            const result = pyodide.runPython(`
import json
json.dumps(lint_code(${JSON.stringify(code)}))
`);
            return JSON.parse(result);
        } catch (e) {
            console.warn("[PythonLinter] Erreur Pyodide, fallback JS", e);
        }
    }

    // Fallback JS
    return lintPythonJS(code);
}

/** Indique si Pyodide est chargé */
export function isPyodideReady() {
    return pyodideInstance !== null;
}

/** Précharger Pyodide en arrière-plan */
export function preloadPyodide() {
    loadPyodideFromCDN();
}

// ── Fallback JS (validateur basique) ────────────────────────────────────────
function lintPythonJS(code) {
    const errors = [];
    const lines = code.split("\n");
    const bracketStack = [];
    const OPEN = {"(": ")", "[": "]", "{": "}"};
    const CLOSE = new Set([")", "]", "}"]);
    let inMultiLineStr = false;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trimStart();
        const num = i + 1;

        if (!trimmed || trimmed.startsWith("#")) continue;
        if (trimmed.includes("LOCK:") && (trimmed.includes(":START") || trimmed.includes(":END"))) continue;

        const tripleDouble = (line.match(/"""/g) || []).length;
        const tripleSingle = (line.match(/'''/g) || []).length;
        if (tripleDouble % 2 === 1 || tripleSingle % 2 === 1) {
            inMultiLineStr = !inMultiLineStr;
            continue;
        }
        if (inMultiLineStr) continue;

        const indent = line.match(/^(\s*)/)[1];
        if (indent.includes("\t")) {
            errors.push({line: num, col: 0, msg: "Tabulation detectee — utiliser 4 espaces", severity: "error"});
        } else if (indent.length % 4 !== 0 && bracketStack.length === 0) {
            errors.push({line: num, col: 0, msg: `Indentation incorrecte (${indent.length} espaces, multiple de 4 attendu)`, severity: "warning"});
        }

        for (const ch of line) {
            if (OPEN[ch]) bracketStack.push({char: ch, close: OPEN[ch], line: num});
            if (CLOSE.has(ch)) {
                if (bracketStack.length === 0) {
                    errors.push({line: num, col: 0, msg: `'${ch}' fermant sans ouverture`, severity: "error"});
                } else {
                    const top = bracketStack.pop();
                    if (top.close !== ch) {
                        errors.push({line: num, col: 0, msg: `'${ch}' ne correspond pas a '${top.char}' ligne ${top.line}`, severity: "error"});
                    }
                }
            }
        }

        if (/^\s*(def|if|elif|else|for|while|class|with|try|except|finally)\b/.test(line)) {
            if (!trimmed.endsWith(":") && !trimmed.endsWith(":\\") && bracketStack.length === 0) {
                errors.push({line: num, col: 0, msg: `':' manquant apres '${trimmed.split(/[\s(]/)[0]}'`, severity: "error"});
            }
        }
    }

    for (const b of bracketStack) {
        errors.push({line: b.line, col: 0, msg: `'${b.char}' jamais ferme`, severity: "error"});
    }

    return errors;
}
